import {
  ampathSystem,
  createCodeableConcept,
  createIdentifier,
  ExtensionUrl,
  getExtensionValue,
  getFirstCodeFromCodableConcepts,
  getIdentifier,
  IdentifierCode,
  isValidReference,
  ServiceRequestCode,
  ServiceRequestIdentifier,
  ServiceRequestPerformerIdentifier,
  system,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { CodeableConcept, Identifier } from 'fhir/r4';
import type { Coding } from '../../domain/schema';
import type { WithId } from '../../types';

import type { PathologyOrder } from '../schema';

export const mapModelToFhirServiceRequest = (
  pathologyOrder: PathologyOrder,
): fhir4.ServiceRequest => {
  const {
    patient,
    requestor,
    sampleDateTime,
    testCodes,
    comment,
    copyDoctors,
    status,
    orderNumber,
  } = pathologyOrder;

  const orderDetail = testCodes.map<CodeableConcept>((test) => {
    const { code, display } = test;
    return createCodeableConcept(code, display, display, ampathSystem);
  });

  return {
    resourceType: 'ServiceRequest',
    subject: patient,
    requester: requestor,
    occurrenceDateTime: sampleDateTime,
    orderDetail,
    note: comment ? [{ text: comment }] : undefined,
    extension: copyDoctors
      ? copyDoctors.map((copyDoctor) => ({
          url: ExtensionUrl.serviceRequestCopyPractitioners,
          valueReference: isValidReference(copyDoctor) ? copyDoctor : undefined,
        }))
      : undefined,
    intent: 'order',
    code: createCodeableConcept(
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      ServiceRequestCode.PathologyRequest,
      system,
    ),
    performer: [
      {
        identifier: createIdentifier(
          IdentifierCode.serviceRequestPerformer,
          ServiceRequestPerformerIdentifier.Ampath,
          ampathSystem,
        ),
      },
    ],
    priority: 'stat',
    status,
    identifier: orderNumber
      ? [
          createIdentifier(
            ServiceRequestIdentifier.AmpathOrderNumber,
            orderNumber,
            ampathSystem,
          ),
        ]
      : undefined,
  };
};

const mapOrderDetailToTestCodes = (
  orderDetail?: CodeableConcept[],
): Coding[] | undefined => {
  if (!orderDetail) {
    return undefined;
  }

  const testCodes = orderDetail?.map<Coding | undefined>((detail) => {
    const code = getFirstCodeFromCodableConcepts([detail]);
    const display = detail.text;

    if (!code) {
      return undefined;
    }

    return {
      code,
      display,
    };
  });

  return compact(testCodes);
};

const getLancetIdentifiers = (identifier: Identifier[] | undefined) => {
  const orderNumber = getIdentifier(
    ServiceRequestIdentifier.LancetOrderNumber,
    identifier,
  );
  const orderId = getIdentifier(
    ServiceRequestIdentifier.LancetOrderId,
    identifier,
  );

  return {
    orderNumber,
    orderId,
  };
};

const getAmpathIdentifiers = (identifier: Identifier[] | undefined) => {
  const orderNumber = getIdentifier(
    ServiceRequestIdentifier.AmpathOrderNumber,
    identifier,
  );

  return {
    orderNumber,
    orderId: 'NA',
  };
};

export const mapFhirServiceRequestToModel = (
  fhir: WithId<fhir4.ServiceRequest>,
): WithId<Partial<PathologyOrder>> => {
  const {
    id,
    subject,
    requester,
    status,
    occurrenceDateTime,
    orderDetail,
    note,
    extension,
    identifier,
  } = fhir;

  const getCopyDoctors = getExtensionValue(
    ExtensionUrl.serviceRequestCopyPractitioners,
  );
  const copyDoctors = extension?.map(getCopyDoctors).filter(isValidReference);

  const { orderNumber, orderId } =
    getIdentifier(ServiceRequestIdentifier.AmpathOrderNumber, identifier) ==
    undefined
      ? getLancetIdentifiers(identifier)
      : getAmpathIdentifiers(identifier);

  return {
    id,
    status,
    sampleDateTime: occurrenceDateTime ? occurrenceDateTime : undefined,
    comment: note ? first(note)?.text : undefined,
    copyDoctors,
    orderNumber,
    orderId,
    requestor: isValidReference(requester) ? requester : undefined,
    patient: isValidReference(subject) ? subject : undefined,
    testCodes: mapOrderDetailToTestCodes(orderDetail),
  };
};
